import {
  mutationOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import React from 'react'
import { useDispatch } from 'react-redux'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { CloseAccountToastMessage } from 'src/app/Toast/Toast'
import { closeModal } from 'src/navigation/Lnk'
import { CloseAccountWriteResponse } from 'src/queries/creditCards/closeAccount/closeAccount.models'
import {
  closeAccount,
  recoverBackendErrorPayload,
} from 'src/queries/creditCards/closeAccount/closeAccount.network'
import { QueryError } from 'src/queries/network.utils'
import {
  CREDIT_CARDS_QK,
  creditCardsQueryKeys,
} from 'src/queries/queries.constants'
import {
  PUT_CLOSE_ACCOUNT_FAILURE,
  PUT_CLOSE_ACCOUNT_INIT,
  PUT_CLOSE_ACCOUNT_SUCCESS,
} from 'src/redux/actions/data/creditCards.actions'

export type CloseAccountVariables = {
  creditCardId: string
  displayName: string
}

export const closeAccountMutations = {
  close: () =>
    mutationOptions<
      CloseAccountWriteResponse,
      QueryError,
      CloseAccountVariables
    >({
      mutationFn: (variables) => {
        // Fail fast rather than PUT /creditcards//close, which the backend
        // would route as a list-level write. The call site guards too, but
        // the factory is the public boundary.
        if (!variables.creditCardId) {
          throw new Error(
            'closeAccountMutations.close: creditCardId is required',
          )
        }
        return closeAccount(variables.creditCardId)
      },
    }),
}

/**
 * Permanently close a credit-card-backed account (the `CloseAccountContent`
 * confirmation popup). Replaces the `putCloseAccountAndCloseModal` thunk.
 * Preserves its Redux lifecycle dispatches (INIT/SUCCESS/FAILURE — still
 * consumed by `closeAccountContent.reducer.ts`'s `puttingCloseAccount`
 * loading flag and `topLevelError`), the success toast (built from the
 * `displayName` + `creditCardId` variables passed in from the call site, not
 * the response — the endpoint only returns an opaque `payload` string), and
 * the modal-close navigation. There is no failure toast, matching the
 * original thunk — the failure path only surfaces `topLevelError` inline on
 * the popup's form.
 *
 * NOTE: despite the name, this does not end the user's session — it closes
 * one credit-card-backed account within the org (the success toast even
 * links to that account's still-viewable Account Overview page), so unlike a
 * session-terminal write, cache invalidation is meaningful here. Adds a
 * NARROW invalidation of the card's own detail key plus every list-shaped
 * credit-card query (mirrors `useDeactivateCreditCard`'s precedent) — never
 * the family root `creditCardsQueryKeys.all`, which would also evict every
 * other card's unrelated detail/permissions cache. There is no
 * `PUT_CLOSE_ACCOUNT_SUCCESS` case in `reactQueryInterop.reducer`, so this is
 * net-new, not a reproduction of an existing interop path. (Cascading
 * effects on budgets/virtual cards under the closed account are outside this
 * slice's scope — no wider invalidation existed before this migration
 * either.)
 *
 * Toast + modal-close live in the useMutation-level onSuccess (not a
 * mutate() call-site callback) since the popup navigates away / unmounts on
 * close, mirroring `useDeactivateCreditCard`'s precedent for this same
 * Redux-toast/connected-router-backed endpoint family. FAILURE is kept at
 * the hook level too, alongside INIT/SUCCESS, so the full lifecycle stays in
 * one place rather than split across the hook and the call site.
 */
export const useCloseAccount = () => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  return useMutation<
    CloseAccountWriteResponse,
    QueryError,
    CloseAccountVariables
  >({
    ...closeAccountMutations.close(),
    onMutate: () => {
      dispatch({ type: PUT_CLOSE_ACCOUNT_INIT })
    },
    onSuccess: (data, variables) => {
      dispatch(closeModal())
      dispatch(
        createSetIsToastOpen(true, {
          toastMessage: (
            <CloseAccountToastMessage
              displayName={variables.displayName}
              id={variables.creditCardId}
            />
          ),
        }),
      )
      dispatch({ type: PUT_CLOSE_ACCOUNT_SUCCESS, payload: data })
      queryClient.invalidateQueries({
        queryKey: creditCardsQueryKeys.detail(variables.creditCardId),
      })
      queryClient.invalidateQueries({
        predicate: (query) => {
          const qkIndex = query.queryKey.indexOf(CREDIT_CARDS_QK)
          return (
            qkIndex !== -1 &&
            typeof query.queryKey[qkIndex + 1] === 'object' &&
            query.queryKey[qkIndex + 1] !== null
          )
        },
      })
    },
    onError: (error) => {
      dispatch({
        type: PUT_CLOSE_ACCOUNT_FAILURE,
        payload: recoverBackendErrorPayload(error.originalError ?? error),
      })
    },
  })
}
